import React, { useCallback, useEffect, useState } from 'react'
import { History, MessageSquare, Plus, Play, CheckCircle2, TrendingUp, ListChecks, AlarmClock, CalendarX, Sparkles } from 'lucide-react'
import { TaskService, type TaskHistoryEvent } from '../../services/taskService'
import { Timestamp } from '../ui/Timestamp'

/**
 * History — what happened to a task, newest first, every time in the
 * user's zone (owner 2026-09-05: "display the task history for when it was
 * created, any progress made, its comments … use the date-time-tz format").
 * Notes are typed here too: the composer at the top is the comments thread.
 */
interface Props {
  taskId: number
  /** Change to refetch (e.g. the task's updated_at). */
  refreshKey?: string | number | null
  canNote?: boolean
  createdAt?: string | null
  updatedAt?: string | null
}

const ICON: Record<string, React.ReactNode> = {
  created: <Sparkles className="h-3.5 w-3.5" />,
  started: <Play className="h-3.5 w-3.5" />,
  completed: <CheckCircle2 className="h-3.5 w-3.5" />,
  progress: <TrendingUp className="h-3.5 w-3.5" />,
  note: <MessageSquare className="h-3.5 w-3.5" />,
  step: <ListChecks className="h-3.5 w-3.5" />,
  reminder: <AlarmClock className="h-3.5 w-3.5" />,
  deferred: <CalendarX className="h-3.5 w-3.5" />,
}
const TONE: Record<string, string> = {
  completed: 'text-ok', started: 'text-brand', deferred: 'text-warn', note: 'text-secondary',
}
const SHOW = 8
// A note or comment in the timeline: the first line, not the essay. The full
// text sits on the title.
const CLIP = 90
const clip = (s: string) => (s.length > CLIP ? `${s.slice(0, CLIP - 1).trimEnd()}…` : s)

export function TaskHistory({ taskId, refreshKey, canNote = true, createdAt, updatedAt }: Props) {
  const [events, setEvents] = useState<TaskHistoryEvent[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [all, setAll] = useState(false)
  const svc = new TaskService()

  const load = useCallback(async () => {
    try {
      setEvents(await new TaskService().getTaskHistory(taskId))
      setErr(null)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load the history')
      setEvents([])
    }
  }, [taskId])
  useEffect(() => { void load() }, [load, refreshKey])

  const addNote = async () => {
    const text = note.trim()
    if (!text) return
    setBusy(true)
    try {
      await svc.addTaskNote(taskId, text)
      setNote('')
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save the note')
    } finally {
      setBusy(false)
    }
  }

  const shown = events ? (all ? events : events.slice(0, SHOW)) : []

  return (
    <section data-testid="task-history">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <h3 className="flex items-center gap-1.5 text-app-meta font-bold uppercase tracking-widest text-secondary">
          <History className="h-3.5 w-3.5 text-brand" /> History
        </h3>
        {(createdAt || updatedAt) && (
          <p className="text-app-micro text-muted" data-testid="task-stamps">
            {createdAt && <span>Created <Timestamp value={createdAt} mode="datetime" /></span>}
            {createdAt && updatedAt && <span> · </span>}
            {updatedAt && <span>Updated <Timestamp value={updatedAt} mode="datetime" /></span>}
          </p>
        )}
      </div>
      {canNote && (
        <div className="mt-2 flex items-start gap-2">
          <Plus className="mt-2 h-3.5 w-3.5 shrink-0 text-muted" aria-hidden="true" />
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void addNote() } }}
            placeholder="Add a note — anything worth remembering about this"
            aria-label="Add a note"
            rows={note ? 3 : 1}
            disabled={busy}
            className="min-w-0 flex-1 resize-y rounded-md border border-transparent bg-transparent px-1 py-1 text-sm text-primary placeholder:text-muted focus:border-soft focus:bg-surface focus:outline-none"
            data-testid="task-note-input"
          />
          {note.trim() && (
            <button type="button" onClick={() => void addNote()} disabled={busy} className="mt-1 text-app-meta font-medium text-brand hover:underline" data-testid="task-note-save">
              {busy ? 'Saving…' : 'Save note'}
            </button>
          )}
        </div>
      )}
      {err && <p className="mt-1 text-xs text-bad" role="alert">{err}</p>}
      {events === null ? (
        <p className="mt-2 text-sm text-muted">Loading…</p>
      ) : events.length === 0 ? (
        <p className="mt-2 text-sm text-muted">Nothing has happened to this yet.</p>
      ) : (
        <ol className="mt-2 space-y-1.5" data-testid="task-history-list">
          {shown.map((e, i) => (
            <li key={`${e.at}-${e.kind}-${i}`} className="flex items-start gap-2 text-sm">
              <span className={`mt-0.5 shrink-0 ${TONE[e.kind] || 'text-muted'}`} aria-hidden="true">{ICON[e.kind] || <History className="h-3.5 w-3.5" />}</span>
              <div className="min-w-0 flex-1">
                <span className="text-primary">{e.title}</span>
                {e.detail && <span className="text-secondary" title={e.detail}> · {clip(e.detail)}</span>}
                <div className="text-app-micro text-muted"><Timestamp value={e.at} mode="datetime" /></div>
              </div>
            </li>
          ))}
        </ol>
      )}
      {events && events.length > SHOW && (
        <button type="button" onClick={() => setAll((a) => !a)} className="mt-2 text-app-meta font-medium text-brand hover:underline">
          {all ? 'Show fewer' : `Show all ${events.length}`}
        </button>
      )}
    </section>
  )
}

export default TaskHistory
